/**
 * CriarPrazoDialog — inclusão de prazo (modelo perícias: buscar antes de criar).
 *
 * Cards por processo (2026-07-12): o número do processo fica visível desde o
 * início; ao digitar (ou quando o staging sugere o CNJ), o sistema BUSCA se o
 * processo já tem card. Achou → o novo prazo entra como NOVO SERVIÇO do card
 * existente, com a escolha de manter o PDF atual dos autos (sem upload) ou
 * substituir pelos autos atualizados. Não achou → fluxo normal (upload
 * obrigatório, staging lê e pré-preenche os campos).
 *
 * Disponível a admin e colaborador (ambos podem incluir na produção).
 */
import { useEffect, useRef, useState } from 'react';
import { useBuscaProcesso, useCriarPrazo, useUploadStaging, useStagingStatus } from '../hooks';
import { SelectPecaBusca } from './SelectPecaBusca';
import { TIPOS_PECA_BY_KEY, type TipoPeca } from '../tiposPeca';
import type { CriarPrazoIn, ProcessoBusca } from '../types';

interface Props {
  onClose: () => void;
}

/** Data de hoje em ISO YYYY-MM-DD (fuso local). */
function hojeIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

/** ISO YYYY-MM-DD → DD/MM/AAAA (para exibir a derivação do vencimento). */
function fmtBr(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** O staging já resolve o tipo canônico do pack para uma `key` do catálogo. */
function pecaPelaKey(key: string | null | undefined): TipoPeca | null {
  if (!key) return null;
  return TIPOS_PECA_BY_KEY[key] ?? null;
}

export function CriarPrazoDialog({ onClose }: Props) {
  const [numero, setNumero] = useState('');
  const [peca, setPeca] = useState<TipoPeca | null>(null);
  const [vencimento, setVencimento] = useState(hojeIso());
  const [sigla, setSigla] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [stagingToken, setStagingToken] = useState<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const jaPreencheu = useRef(false);

  // Cards por processo — novo serviço num card existente
  const [processoSel, setProcessoSel] = useState<ProcessoBusca | null>(null);
  const [ignorarMatch, setIgnorarMatch] = useState(false);
  const [pdfAcao, setPdfAcao] = useState<'manter' | 'substituir'>('manter');

  const mut = useCriarPrazo();
  const uploadMut = useUploadStaging();
  const { data: staging } = useStagingStatus(stagingToken);
  const { data: matches } = useBuscaProcesso(numero);

  // Auto-preenche quando o staging termina — só UMA vez (não sobrescreve edição).
  useEffect(() => {
    if (staging?.status === 'pronto' && !jaPreencheu.current) {
      jaPreencheu.current = true;
      if (staging.numero_processo_sugerido) setNumero(staging.numero_processo_sugerido);
      if (staging.tribunal_sugerido) setSigla(staging.tribunal_sugerido);
      if (staging.vencimento_sugerido) setVencimento(staging.vencimento_sugerido);
      const sugerida = pecaPelaKey(staging.tipo_peca_sugerido);
      if (sugerida) setPeca(sugerida);
    }
  }, [staging]);

  async function handleArquivo(file: File) {
    setErro(null);
    setNomeArquivo(file.name);
    setUploadPct(0);
    jaPreencheu.current = false;
    try {
      const { token } = await uploadMut.mutateAsync({
        arquivo: file,
        onProgress: setUploadPct,
      });
      setStagingToken(token);
    } catch (err: any) {
      if (err?.status === 413) setErro('PDF muito grande (limite 500 MB).');
      else setErro(err?.message ?? 'Erro ao enviar o PDF dos autos');
      setNomeArquivo(null);
    }
  }

  function escolherProcesso(p: ProcessoBusca) {
    setProcessoSel(p);
    setIgnorarMatch(false);
    setNumero(p.numero_processo);
    if (p.sigla_tribunal) setSigla(p.sigla_tribunal);
    setPdfAcao(p.tem_pdf ? 'manter' : 'substituir');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!numero.trim() || !peca || !vencimento) return;
    if (processoSel && pdfAcao === 'substituir' && !stagingToken) {
      setErro('Para substituir os autos, envie o PDF atualizado.');
      return;
    }
    setErro(null);
    const body: CriarPrazoIn = {
      numero_processo: numero.trim(),
      tipo_prazo: peca.rotulo,
      data_vencimento: vencimento,
      sigla_tribunal: sigla.trim() || null,
      observacao: obs.trim() || null,
      staging_token: stagingToken,
      tipo_peca_escolhida: peca.key,
      processo_id: processoSel?.processo_id ?? null,
      pdf_acao: processoSel ? pdfAcao : null,
    };
    try {
      await mut.mutateAsync(body);
      onClose();
    } catch (err: any) {
      if (err.status === 409) setErro('Já existe um prazo com esse número/tipo.');
      else if (err.status === 403) setErro('Sem permissão para criar prazos.');
      else setErro(err?.message ?? 'Erro ao criar o prazo');
    }
  }

  const inputClasse =
    'w-full text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

  const enviando = uploadMut.isPending;              // POST do arquivo em andamento
  const processando = staging?.status === 'processando';  // leitura rápida rodando
  // Sem card existente: upload OBRIGATÓRIO — campos só após a leitura (fluxo
  // original). Novo serviço em card existente: campos aparecem já (o processo
  // é conhecido); upload só se for substituir os autos.
  const stagingConcluiu = staging?.status === 'pronto' || staging?.status === 'erro';
  const mostrarCampos = stagingConcluiu || !!processoSel;
  const mostrarUpload = !processoSel || pdfAcao === 'substituir';
  // Banner de matches: só quando ainda não escolheu e não descartou.
  const candidatos = !processoSel && !ignorarMatch ? (matches ?? []) : [];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
            ➕ Novo Prazo
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Número do processo — visível desde o início: dispara a busca de
              card existente (digitado OU sugerido pelo staging). */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Número do processo *
            </label>
            <input
              value={numero}
              onChange={(e) => {
                setNumero(e.target.value);
                setProcessoSel(null);
                setIgnorarMatch(false);
              }}
              placeholder="0000000-00.0000.5.01.0000"
              className={inputClasse}
            />
          </div>

          {/* Processo(s) já cadastrado(s) → adicionar como novo serviço */}
          {candidatos.length > 0 && (
            <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded p-2 space-y-1.5">
              <p className="text-[11px] font-medium text-amber-800 dark:text-amber-300">
                ⚖ Este processo já tem card — adicionar como novo serviço:
              </p>
              {candidatos.map((p) => (
                <button
                  key={p.processo_id}
                  type="button"
                  onClick={() => escolherProcesso(p)}
                  className="w-full text-left text-xs bg-white dark:bg-gray-700 border border-amber-200 dark:border-amber-800 rounded p-2 hover:border-amber-400 transition"
                >
                  <span className="font-mono">{p.numero_processo}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}· {p.servicos_total} serviço{p.servicos_total > 1 ? 's' : ''}
                    {p.pdf_atualizado_em ? ` · PDF de ${fmtBr(p.pdf_atualizado_em)}` : (p.tem_pdf ? ' · com PDF' : ' · sem PDF')}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIgnorarMatch(true)}
                className="text-[11px] text-gray-500 dark:text-gray-400 underline"
              >
                …ou cadastrar como processo novo →
              </button>
            </div>
          )}

          {/* Novo serviço no card existente — manter ou substituir o PDF */}
          {processoSel && (
            <div className="border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 rounded p-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-medium text-blue-800 dark:text-blue-300">
                  ⚖ Novo serviço em <span className="font-mono">{processoSel.numero_processo}</span>{' '}
                  ({processoSel.servicos_total} serviço{processoSel.servicos_total > 1 ? 's' : ''})
                </p>
                <button
                  type="button"
                  onClick={() => { setProcessoSel(null); setIgnorarMatch(true); }}
                  className="text-[11px] text-gray-400 underline"
                >
                  desfazer
                </button>
              </div>
              {processoSel.tem_pdf ? (
                <div className="space-y-1 text-[11px] text-gray-700 dark:text-gray-300">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={pdfAcao === 'manter'}
                      onChange={() => setPdfAcao('manter')}
                    />
                    Manter PDF atual dos autos
                    {processoSel.pdf_atualizado_em && (
                      <span className="text-gray-400">(de {fmtBr(processoSel.pdf_atualizado_em)})</span>
                    )}
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={pdfAcao === 'substituir'}
                      onChange={() => setPdfAcao('substituir')}
                    />
                    Substituir pelos autos atualizados (upload)
                  </label>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">
                    Substituir troca só o PDF — as peças já geradas nos outros serviços ficam intactas.
                  </p>
                </div>
              ) : (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Este card ainda não tem PDF — envie os autos abaixo.
                </p>
              )}
            </div>
          )}

          {/* Upload dos autos — obrigatório no processo novo; no serviço novo,
              só quando "substituir" (ou card sem PDF). */}
          {mostrarUpload && (
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {processoSel
                ? 'PDF dos autos atualizados (obrigatório para substituir)'
                : 'PDF dos autos (obrigatório — lê e preenche os campos)'}
            </label>
            <label
              className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded p-3 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
            >
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleArquivo(f);
                }}
              />
              {nomeArquivo ? `📄 ${nomeArquivo}` : '📎 Selecionar PDF dos autos'}
            </label>
            {/* Fase 1: upload do arquivo (barra simples com %) */}
            {enviando && (
              <div className="mt-2">
                <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${Math.max(4, uploadPct)}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  Enviando o PDF… {uploadPct}%
                </p>
              </div>
            )}
            {/* Fase 2: leitura rápida (só metadados, SEM OCR pesado) — indicador simples.
                O OCR/split/pdftotext de verdade roda DEPOIS, no card, ao "Incluir". */}
            {!enviando && processando && (
              <p className="text-[11px] text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1.5">
                <span className="inline-block animate-spin">◠</span>
                Lendo os dados do processo…
              </p>
            )}
            {staging?.status === 'erro' && (
              <p className="text-[11px] text-red-500 mt-1">
                ⚠ Falha ao ler os autos ({staging.erro_msg || 'erro desconhecido'}) — preencha manualmente.
              </p>
            )}
            {staging?.status === 'pronto' && (
              <p className="text-[11px] text-green-600 dark:text-green-400 mt-1">
                ✓ Dados lidos — confira os campos. O OCR completo roda no card, na Fila, após "Incluir prazo".
              </p>
            )}
          </div>
          )}

          {mostrarCampos && (<>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Tipo de manifestação *
              </label>
              <SelectPecaBusca value={peca} onChange={setPeca} className={inputClasse} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Vencimento *
              </label>
              <input
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
                className={inputClasse}
              />
            </div>
          </div>
          {staging?.status === 'pronto' && staging.vencimento_sugerido
            && staging.vencimento_base && staging.vencimento_dias_uteis ? (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 -mt-1">
              📅 {staging.vencimento_fonte === 'djen' ? 'pelo DJEN — intimação' : 'sugerido: publicação'}{' '}
              {fmtBr(staging.vencimento_base)} + {staging.vencimento_dias_uteis} dias úteis —{' '}
              <span className="italic">confira</span>
            </p>
          ) : null}

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Sigla do tribunal (opcional)
            </label>
            <input
              value={sigla}
              onChange={(e) => setSigla(e.target.value)}
              placeholder="TRT1"
              className={inputClasse}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Observação (opcional)
            </label>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={2}
              className={`${inputClasse} resize-none`}
            />
          </div>
          </>)}

          {erro && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
              ⚠ {erro}
            </div>
          )}

          {mostrarCampos && (
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={mut.isPending}
                className="flex-1 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={mut.isPending || !numero.trim() || !peca || !vencimento}
                className="flex-1 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-medium"
              >
                {mut.isPending
                  ? '⏳ Criando…'
                  : processoSel ? '✅ Incluir serviço' : '✅ Incluir prazo'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
